// World planner (ADR 0020): turns a world description and a track into what gets drawn: road spans, water bodies,
// buildings, furniture and landmarks. Pure and deterministic, no three.js: a world is checked on the server exactly as it
// is built in the browser. Every count here is bounded by the budget, whatever the description asks for.
import { trackAt, mod, courseSamples, type Track } from '@gruvetto/core';
import type { LandmarkPart, WorldContent, WorldStretch } from '@gruvetto/contracts';
import { DISTRICTS, LANDMARKS, LANDMARK_COLORS, THEME_WORLDS, type DistrictStyle } from './presets.js';

export interface WorldBudget {
  buildings: number;
  furniture: number;
  landmarks: number;
  skyline: number;
}
export const DEFAULT_BUDGET: WorldBudget = { buildings: 1800, furniture: 1600, landmarks: 240, skyline: 140 };

/** Half width of the road surface (m), the same as the renderer's. */
export const ROAD_HALF = 4.8;
/** Height clear of a rider under a gate (m). */
const GATE_CLEARANCE = 5.2;
const TUNNEL_MIN = 40,
  LINE_CLEAR = 45;

export interface RoadSpan {
  /** From, to (m along the lap). */
  s0: number;
  s1: number;
  surface: NonNullable<WorldStretch['surface']>;
  color?: string;
  markings: boolean;
  edge: NonNullable<WorldStretch['edge']>;
  structure: NonNullable<WorldStretch['structure']>;
  bridge: NonNullable<WorldStretch['bridge']>;
  district: number;
  crowd: number;
}
export interface PlannedBuilding {
  x: number;
  z: number;
  yaw: number;
  w: number;
  d: number;
  h: number;
  floors: number;
  facade: string;
  roofColor: string;
  windows: DistrictStyle['windows'];
  roof: DistrictStyle['roof'];
  shops: boolean;
  seed: number;
}
export type FurnitureKind = 'lamp' | 'car' | 'bench' | 'tree';
export interface PlannedFurniture {
  kind: FurnitureKind;
  x: number;
  z: number;
  yaw: number;
  seed: number;
}
export interface PlannedLandmark {
  parts: LandmarkPart[];
  x: number;
  z: number;
  yaw: number;
  scale: number;
  radius: number;
  group: number;
  over: boolean;
  s: number;
}
export type PlannedWater =
  | { kind: 'lake'; x: number; z: number; r: number; level: number; color?: string; seed: number }
  | { kind: 'sea'; nx: number; nz: number; line: number; level: number; color?: string }
  | { kind: 'river'; path: [number, number][]; width: number; level: number; color?: string };

/** Nearest road to a point: its distance, the abscissa of the nearest point along the lap (-1 far away) and its height. */
export interface RoadQuery { d: number; s: number; y: number }

export interface WorldPlan {
  world: WorldContent;
  horizon: NonNullable<WorldContent['horizon']>;
  styles: DistrictStyle[];
  spans: RoadSpan[];
  water: PlannedWater[];
  buildings: PlannedBuilding[];
  furniture: PlannedFurniture[];
  landmarks: PlannedLandmark[];
  skyline: { x: number; z: number; w: number; d: number; h: number; facade: string }[];
  /** Span at a distance along the lap. */
  spanAt(s: number): RoadSpan;
  /** Distance to the road, nearest point along it and road height there. */
  road(x: number, z: number): RoadQuery;
  /** District of the road nearest to (x, z); the base district far from it. `near` is a `road(x, z)` already computed. */
  styleAt(x: number, z: number, near?: RoadQuery): DistrictStyle;
  /** Building or landmark footprint at (x, z), with a margin. */
  occupied(x: number, z: number, margin?: number): boolean;
  /** Water body at (x, z): signed distance to its bank (negative in the water) and level; null when far from any. */
  waterAt(x: number, z: number): { bank: number; level: number; width: number } | null;
}

const hash = (a: number, b: number) => {
  const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return s - Math.floor(s);
};
function fnv(str: string) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % 100000;
}
const pick = <T>(a: readonly T[], r: number): T => a[Math.min(a.length - 1, Math.floor(r * a.length))];
const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));

/** The world of a track: its own, or the one its theme stands for, or none (the original generic countryside). */
export function worldOf(c: { world?: WorldContent; theme?: string }): WorldContent | null {
  return c.world ?? (c.theme ? (THEME_WORLDS[c.theme] ?? null) : null);
}

/** Style of a district name: a custom district of the world, a preset, or null. */
export function resolveDistrict(world: WorldContent, id: string | undefined): DistrictStyle | null {
  if (!id) return DISTRICTS.countryside;
  const c = world.districts?.find(d => d.id === id);
  if (c) {
    const b = DISTRICTS[c.preset];
    return {
      ...b,
      ...(c.floors ? { floors: [Math.min(...c.floors), Math.max(...c.floors)] as [number, number] } : {}),
      ...(c.density != null ? { density: c.density } : {}),
      ...(c.setback != null ? { setback: c.setback } : {}),
      ...(c.facades ? { facades: c.facades } : {}),
      ...(c.roofs ? { roofs: c.roofs } : {}),
      ...(c.windows ? { windows: c.windows } : {}),
      ...(c.roof ? { roof: c.roof } : {}),
      ...(c.shops != null ? { shops: c.shops } : {}),
      ...(c.ground ? { ground: c.ground, groundMix: Math.max(b.groundMix, 0.85) } : {}),
      ...(c.trees != null ? { trees: c.trees } : {}),
      ...(c.relief != null ? { relief: c.relief } : {}),
    };
  }
  return (DISTRICTS as Record<string, DistrictStyle>)[id] ?? null;
}

/** Parts of a landmark, `main` replaced by its colour. */
export function landmarkParts(l: NonNullable<WorldContent['landmarks']>[number]): LandmarkPart[] {
  if (l.parts) return l.parts;
  const main = l.color ?? LANDMARK_COLORS[l.preset!] ?? '#c9c2b4';
  return LANDMARKS[l.preset!].map(p => (p.color === 'main' ? { ...p, color: main } : p));
}

/** Road index for distance queries: points every few metres in 60 m cells. */
function roadIndex(tr: Track) {
  const CELL = 60,
    stride = Math.max(1, Math.round(3 / tr.step)),
    grid = new Map<number, number[]>();
  const key = (cx: number, cz: number) => (cx + 32768) * 65536 + (cz + 32768);
  for (let i = 0; i < tr.N; i += stride) {
    const k = key(Math.floor(tr.x[i] / CELL), Math.floor(tr.z[i] / CELL));
    const c = grid.get(k);
    if (c) c.push(i);
    else grid.set(k, [i]);
  }
  let x0 = 1e9,
    x1 = -1e9,
    z0 = 1e9,
    z1 = -1e9,
    y0 = 1e9;
  for (let i = 0; i < tr.N; i += stride) {
    x0 = Math.min(x0, tr.x[i]);
    x1 = Math.max(x1, tr.x[i]);
    z0 = Math.min(z0, tr.z[i]);
    z1 = Math.max(z1, tr.z[i]);
    y0 = Math.min(y0, tr.y[i]);
  }
  const FAR = 480,
    far = { d: 999, s: -1, y: y0 };
  return (x: number, z: number): { d: number; s: number; y: number } => {
    // the terrain asks for every vertex out to the mountains: anything this far is simply "far" (s = -1)
    if (x < x0 - FAR || x > x1 + FAR || z < z0 - FAR || z > z1 + FAR) return far;
    const cx = Math.floor(x / CELL),
      cz = Math.floor(z / CELL);
    let best = 1e18,
      bi = -1;
    for (let r = 0; r <= FAR / CELL; r++) {
      // rings outwards; stop once a point is found and the next ring cannot hold a nearer one
      for (let i = -r; i <= r; i++)
        for (let j = -r; j <= r; j++) {
          if (Math.max(Math.abs(i), Math.abs(j)) !== r) continue;
          const c = grid.get(key(cx + i, cz + j));
          if (!c) continue;
          for (const k of c) {
            const dx = tr.x[k] - x,
              dz = tr.z[k] - z,
              d = dx * dx + dz * dz;
            if (d < best) {
              best = d;
              bi = k;
            }
          }
        }
      if (bi >= 0 && r * CELL * r * CELL > best) break;
    }
    if (bi < 0) return far;
    return { d: Math.sqrt(best), s: bi * tr.step, y: tr.y[bi] };
  };
}

/** A rectangle's corners. */
function corners(b: { x: number; z: number; yaw: number; w: number; d: number }) {
  const c = Math.cos(b.yaw),
    s = Math.sin(b.yaw),
    out: [number, number][] = [];
  for (const [u, v] of [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ]) {
    const lx = (u * b.w) / 2,
      lz = (v * b.d) / 2;
    out.push([b.x + lx * c + lz * s, b.z - lx * s + lz * c]);
  }
  return out;
}
/** Separating axis test between two oriented rectangles. */
function overlap(a: [number, number][], b: [number, number][]) {
  for (const poly of [a, b])
    for (let i = 0; i < 2; i++) {
      const nx = poly[i + 1][1] - poly[i][1],
        nz = poly[i][0] - poly[i + 1][0];
      let a0 = 1e18,
        a1 = -1e18,
        b0 = 1e18,
        b1 = -1e18;
      for (const p of a) {
        const t = p[0] * nx + p[1] * nz;
        a0 = Math.min(a0, t);
        a1 = Math.max(a1, t);
      }
      for (const p of b) {
        const t = p[0] * nx + p[1] * nz;
        b0 = Math.min(b0, t);
        b1 = Math.max(b1, t);
      }
      if (a1 < b0 || b1 < a0) return false;
    }
  return true;
}

class Footprints {
  private cells = new Map<number, { poly: [number, number][]; yaw: number; x: number; z: number; w: number; d: number }[]>();
  private static CELL = 40;
  private key(x: number, z: number) {
    return (Math.floor(x / Footprints.CELL) + 8192) * 16384 + Math.floor(z / Footprints.CELL) + 8192;
  }
  private near(x: number, z: number, r: number) {
    const out = new Set<{ poly: [number, number][]; yaw: number; x: number; z: number; w: number; d: number }>(),
      C = Footprints.CELL;
    for (let i = Math.floor((x - r) / C); i <= Math.floor((x + r) / C); i++)
      for (let j = Math.floor((z - r) / C); j <= Math.floor((z + r) / C); j++) for (const e of this.cells.get((i + 8192) * 16384 + j + 8192) ?? []) out.add(e);
    return out;
  }
  hits(b: { x: number; z: number; yaw: number; w: number; d: number }) {
    const poly = corners(b),
      r = Math.hypot(b.w, b.d) / 2;
    for (const e of this.near(b.x, b.z, r)) if (overlap(poly, e.poly)) return true;
    return false;
  }
  add(b: { x: number; z: number; yaw: number; w: number; d: number }) {
    const e = { poly: corners(b), yaw: b.yaw, x: b.x, z: b.z, w: b.w, d: b.d },
      C = Footprints.CELL,
      r = Math.hypot(b.w, b.d) / 2;
    for (let i = Math.floor((b.x - r) / C); i <= Math.floor((b.x + r) / C); i++)
      for (let j = Math.floor((b.z - r) / C); j <= Math.floor((b.z + r) / C); j++) {
        const k = (i + 8192) * 16384 + j + 8192,
          c = this.cells.get(k);
        if (c) c.push(e);
        else this.cells.set(k, [e]);
      }
  }
  at(x: number, z: number, m: number) {
    for (const e of this.near(x, z, m + 1)) {
      const c = Math.cos(e.yaw),
        s = Math.sin(e.yaw),
        dx = x - e.x,
        dz = z - e.z,
        lx = dx * c - dz * s,
        lz = dx * s + dz * c;
      if (Math.abs(lx) <= e.w / 2 + m && Math.abs(lz) <= e.d / 2 + m) return true;
    }
    return false;
  }
}

/** Paints a value on the road samples of a stretch (it may wrap over the line). `N`: samples of the course. */
function paint<T>(arr: T[], N: number, from: number, to: number, v: T) {
  const a = Math.floor(from * N),
    b = Math.ceil(to * N);
  if (to > from) {
    for (let i = a; i < Math.min(b, N); i++) arr[i] = v;
    return;
  }
  for (let i = a; i < N; i++) arr[i] = v;
  for (let i = 0; i < Math.min(b, N); i++) arr[i] = v;
}

/** An open road's run-out carries on the look of its finish, its lead-in the look of its start (ADR 0029). */
function extendEnds<T>(arr: T[], course: number, lead: number) {
  const N = arr.length;
  if (course >= N) return;
  for (let i = course; i < N; i++) arr[i] = arr[i < N - lead ? course - 1 : 0];
}

export function planWorld(world: WorldContent, tr: Track, budget: WorldBudget = DEFAULT_BUDGET): WorldPlan {
  // positions along the lap are fractions of the course: a whole loop, or an open road from its start to its finish
  const seed = fnv(tr.id),
    N = tr.N,
    C = courseSamples(tr),
    road = roadIndex(tr);
  let minX = 1e9,
    maxX = -1e9,
    minZ = 1e9,
    maxZ = -1e9,
    minY = 1e9;
  for (let i = 0; i < N; i++) {
    minX = Math.min(minX, tr.x[i]);
    maxX = Math.max(maxX, tr.x[i]);
    minZ = Math.min(minZ, tr.z[i]);
    maxZ = Math.max(maxZ, tr.z[i]);
    minY = Math.min(minY, tr.y[i]);
  }
  const cx = (minX + maxX) / 2,
    cz = (minZ + maxZ) / 2,
    extent = Math.max(maxX - minX, maxZ - minZ) / 2 + 380;

  // ---- districts ----
  const styles: DistrictStyle[] = [],
    names = new Map<string, number>();
  const styleIdx = (id: string | undefined) => {
    const key = id ?? '';
    if (names.has(key)) return names.get(key)!;
    const st = resolveDistrict(world, id) ?? DISTRICTS.countryside;
    styles.push(st);
    names.set(key, styles.length - 1);
    return styles.length - 1;
  };
  const base = styleIdx(world.base);

  // ---- samples of the road: one entry per sample, painted in order, later stretches win ----
  const district = new Array<number>(N).fill(base);
  const surface = new Array<RoadSpan['surface'] | null>(N).fill(null),
    edge = new Array<RoadSpan['edge'] | null>(N).fill(null);
  const markings = new Array<boolean | null>(N).fill(null),
    color = new Array<string | undefined>(N).fill(undefined),
    crowd = new Array<number | null>(N).fill(null);
  const structure = new Array<RoadSpan['structure']>(N).fill('ground'),
    bridge = new Array<RoadSpan['bridge'] | null>(N).fill(null);
  for (const st of world.stretches ?? []) {
    if (st.from === st.to) continue;
    if (st.district) paint(district, C, st.from, st.to, styleIdx(st.district));
    if (st.surface) paint(surface, C, st.from, st.to, st.surface);
    if (st.edge) paint(edge, C, st.from, st.to, st.edge);
    if (st.markings != null) paint(markings, C, st.from, st.to, st.markings);
    if (st.color) paint(color, C, st.from, st.to, st.color);
    if (st.crowd != null) paint(crowd, C, st.from, st.to, st.crowd);
    if (st.structure) paint(structure, C, st.from, st.to, st.structure);
    if (st.bridge) paint(bridge, C, st.from, st.to, st.bridge);
  }
  for (const arr of [district, surface, edge, markings, color, crowd, structure, bridge] as unknown[][]) extendEnds(arr, C, Math.round(tr.lead / tr.step));

  // ---- water ----
  const water: PlannedWater[] = [];
  (world.water ?? []).forEach((w, n) => {
    const a = mod(w.at, 1) * tr.lap,
      p = trackAt(tr, a),
      sign = w.side === 'left' ? -1 : 1;
    const nx = -p.tz * sign,
      nz = p.tx * sign;
    if (w.kind === 'lake') {
      let r = w.size ?? 150,
        off = (w.distance ?? 40) + r,
        x = 0,
        z = 0;
      // a lake never swallows the road: it moves away, then shrinks
      for (let k = 0; k < 16; k++) {
        x = p.x + nx * off;
        z = p.z + nz * off;
        const clear = road(x, z).d - ROAD_HALF - 14;
        if (clear >= r * 1.25) break;
        if (k < 8) off += r * 0.3;
        else r = Math.max(20, clear / 1.25);
      }
      let level = 1e9;
      for (let i = 0; i < N; i += 4) if (Math.hypot(tr.x[i] - x, tr.z[i] - z) < r + 160) level = Math.min(level, tr.y[i]);
      water.push({ kind: 'lake', x, z, r, level: (level > 1e8 ? p.y : level) - 2.5, color: w.color, seed: seed + n });
    } else if (w.kind === 'sea') {
      // a shoreline beyond the whole track: the sea never reaches the road
      const dx = p.x - cx,
        dz = p.z - cz,
        h = Math.hypot(dx, dz) || 1,
        ux = dx / h,
        uz = dz / h;
      let line = -1e9;
      for (let i = 0; i < N; i += 2) line = Math.max(line, (tr.x[i] - cx) * ux + (tr.z[i] - cz) * uz);
      water.push({ kind: 'sea', nx: ux, nz: uz, line: line + clamp(w.distance ?? 40, 14, 800), level: minY - 3, color: w.color });
    } else {
      const width = clamp(w.size ?? 40, 10, 400),
        path: [number, number][] = [],
        reach = extent * 2.2;
      // from the crossing outwards in both directions, heading drifting slowly
      for (const dir of [-1, 1]) {
        let x = p.x,
          z = p.z,
          head = Math.atan2(-p.tz, p.tx) + (dir < 0 ? Math.PI : 0);
        const part: [number, number][] = [];
        for (let t = 0; t < reach; t += 30) {
          head += (hash(seed + n, t * dir) - 0.5) * 0.22 * (t < width + 40 ? 0 : 1);
          x += Math.cos(head) * 30;
          z += Math.sin(head) * 30;
          part.push([x, z]);
        }
        if (dir < 0) path.push(...part.reverse(), [p.x, p.z]);
        else path.push(...part);
      }
      // a bridge wherever the road meets the river, and the water well below the lowest of them
      let level = 1e9;
      const near = (i: number) => {
        let b = 1e18;
        for (const q of path) {
          const d = (q[0] - tr.x[i]) ** 2 + (q[1] - tr.z[i]) ** 2;
          if (d < b) b = d;
        }
        return Math.sqrt(b);
      };
      const auto = width < 40 ? 'stone' : width > 150 ? 'suspension' : 'steel';
      for (let i = 0; i < N; i += 2) {
        if (near(i) > width / 2 + 30) continue;
        level = Math.min(level, tr.y[i]);
        if (structure[i] !== 'tunnel') {
          for (let k = -2; k < 2; k++) {
            const j = mod(i + k, N);
            structure[j] = 'bridge';
            bridge[j] ??= auto;
          }
        }
      }
      water.push({ kind: 'river', path, width, level: (level > 1e8 ? p.y : level) - 6, color: w.color });
    }
  });
  // a tunnel needs room for its portals, and no structure sits on the start line (nor past an open road's finish)
  for (let i = 0; i < N; i++) {
    const s = i * tr.step;
    if ((s < LINE_CLEAR || s > tr.lap - LINE_CLEAR) && structure[i] === 'tunnel') structure[i] = 'ground';
  }

  // ---- spans: runs of identical samples ----
  const spans: RoadSpan[] = [],
    sign = (i: number) => {
      const st = styles[district[i]],
        s = surface[i] ?? st.surface;
      return {
        surface: s,
        color: color[i],
        markings: markings[i] ?? (s === 'asphalt' || s === 'smooth' || s === 'concrete'),
        edge: edge[i] ?? (structure[i] === 'bridge' ? 'railing' : structure[i] === 'tunnel' ? 'wall' : st.edge),
        structure: structure[i],
        bridge: bridge[i] ?? 'stone',
        district: district[i],
        crowd: crowd[i] ?? st.crowd,
      };
    };
  let run = sign(0),
    runFrom = 0,
    runKey = JSON.stringify(run);
  for (let i = 1; i <= N; i++) {
    const cur = i < N ? sign(i) : null,
      key = cur ? JSON.stringify(cur) : '';
    if (key !== runKey) {
      spans.push({ s0: runFrom * tr.step, s1: i * tr.step, ...run });
      if (cur) {
        run = cur;
        runKey = key;
        runFrom = i;
      }
    }
  }
  // a tunnel too short to hold its portals becomes open road
  for (const sp of spans) if (sp.structure === 'tunnel' && sp.s1 - sp.s0 < TUNNEL_MIN) sp.structure = 'ground';
  const spanAt = (s: number) => {
    const x = mod(s, tr.L);
    let lo = 0,
      hi = spans.length - 1;
    while (lo < hi) {
      const m = (lo + hi) >> 1;
      if (spans[m].s1 <= x) lo = m + 1;
      else hi = m;
    }
    return spans[lo];
  };

  // the terrain asks at every vertex: each river's segments are indexed by the cells they can matter to
  const RIVER_CELL = 150,
    riverKey = (x: number, z: number) => (Math.floor(x / RIVER_CELL) + 8192) * 16384 + Math.floor(z / RIVER_CELL) + 8192;
  const riverCells = new Map<PlannedWater, Map<number, number[]>>();
  for (const w of water) {
    if (w.kind !== 'river') continue;
    const cells = new Map<number, number[]>(),
      reach = w.width / 2 + 280;
    for (let i = 0; i < w.path.length - 1; i++) {
      const [ax, az] = w.path[i];
      for (let x = ax - reach; x <= ax + reach + RIVER_CELL; x += RIVER_CELL)
        for (let z = az - reach; z <= az + reach + RIVER_CELL; z += RIVER_CELL) {
          const k = riverKey(x, z),
            c = cells.get(k);
          if (!c) cells.set(k, [i]);
          else c.push(i);
        }
    }
    riverCells.set(w, cells);
  }
  const waterAt: WorldPlan['waterAt'] = (x, z) => {
    let best: { bank: number; level: number; width: number } | null = null;
    for (const w of water) {
      let bank: number,
        width = 0;
      if (w.kind === 'lake') {
        const a = Math.atan2(z - w.z, x - w.x),
          wobble = 1 + 0.22 * (Math.sin(a * 3 + w.seed) * 0.6 + Math.sin(a * 5 + w.seed * 2.1) * 0.4);
        bank = Math.hypot(x - w.x, z - w.z) - w.r * wobble;
      } else if (w.kind === 'sea') {
        const along = (x - cx) * -w.nz + (z - cz) * w.nx,
          wob = Math.sin(along / 170 + seed) * 22 + Math.sin(along / 61) * 7;
        bank = w.line + wob - ((x - cx) * w.nx + (z - cz) * w.nz);
      } else {
        let b = 1e18;
        for (const i of riverCells.get(w)?.get(riverKey(x, z)) ?? []) {
          const [ax, az] = w.path[i],
            [bx, bz] = w.path[i + 1],
            dx = bx - ax,
            dz = bz - az,
            t = clamp(((x - ax) * dx + (z - az) * dz) / (dx * dx + dz * dz), 0, 1);
          b = Math.min(b, (ax + dx * t - x) ** 2 + (az + dz * t - z) ** 2);
        }
        bank = Math.sqrt(b) - w.width / 2;
        width = w.width;
      }
      if (bank < 260 && (!best || bank < best.bank)) best = { bank, level: w.level, width };
    }
    return best;
  };
  const wet = (x: number, z: number, m: number) => {
    const w = waterAt(x, z);
    return !!w && w.bank < m;
  };

  // ---- landmarks first: buildings make way for them ----
  const prints = new Footprints(),
    landmarks: PlannedLandmark[] = [];
  (world.landmarks ?? []).forEach((l, group) => {
    const scale = l.scale ?? 1,
      full = landmarkParts(l);
    const positions: number[] = [];
    if (l.every) {
      // round a loop back to the start; an open road ends at its finish
      const P = tr.lap,
        from = l.at * P,
        end = l.until != null ? l.until * P : tr.open ? P : P + from - l.every / 2,
        stop = tr.open ? Math.max(end, from) : end < from ? end + P : end;
      for (let s = from; s < stop && positions.length < 100; s += l.every) positions.push(tr.open ? s : mod(s, P));
    } else positions.push(l.at * tr.lap);
    for (const s of positions) {
      if (landmarks.length >= budget.landmarks) return;
      const p = trackAt(tr, s),
        yaw = Math.atan2(p.tx, p.tz);
      if (l.over) {
        if (spanAt(s).structure === 'tunnel') continue;
        // nothing a rider could ride into: above the clearance, or beyond the road edges
        const parts = full.filter(q => (q.y ?? 0) * scale >= GATE_CLEARANCE || (Math.abs(q.x ?? 0) - q.w / 2) * scale > ROAD_HALF + 0.5);
        if (parts.length) landmarks.push({ parts, x: p.x, z: p.z, yaw, scale, radius: ROAD_HALF, group, over: true, s });
        continue;
      }
      const radius = full.reduce((m, q) => Math.max(m, Math.hypot(Math.abs(q.x ?? 0) + q.w / 2, Math.abs(q.z ?? 0) + (q.d ?? q.w) / 2)), 1) * scale;
      const side = l.side === 'left' ? -1 : l.side === 'right' ? 1 : hash(seed + group, s) < 0.5 ? -1 : 1;
      let off = ROAD_HALF + Math.max(l.distance ?? 12, 3) + radius,
        x = 0,
        z = 0,
        ok = false;
      for (let k = 0; k < 12 && !ok; k++) {
        x = p.x - p.tz * side * off;
        z = p.z + p.tx * side * off;
        ok = road(x, z).d > radius + ROAD_HALF + 2.5 && !wet(x, z, radius * 0.7);
        if (!ok) off += Math.max(radius * 0.4, 10);
      }
      if (!ok) continue;
      landmarks.push({ parts: full, x, z, yaw, scale, radius, group, over: false, s });
      prints.add({ x, z, yaw: 0, w: radius * 2, d: radius * 2 });
    }
  });

  // ---- buildings, row after row: the budget keeps those seen from the road ----
  const buildings: PlannedBuilding[] = [];
  const maxRows = styles.reduce((m, st) => Math.max(m, st.density > 0 ? st.rows : 0), 0);
  for (let row = 0; row < maxRows && buildings.length < budget.buildings; row++)
    for (const side of [-1, 1]) {
      let s = hash(seed, side + row) * 12,
        offBase: number,
        n = 0;
      while (s < tr.L && buildings.length < budget.buildings) {
        const sp = spanAt(s),
          st = styles[sp.district],
          h1 = hash(seed + n + row * 91, s),
          h2 = hash(s, seed + side),
          h3 = hash(s * 1.7, n);
        n++;
        const w = st.plot[0] * (0.8 + h1 * 0.5),
          d = st.plot[1] * (0.85 + h2 * 0.5),
          step = w + (st.density > 0.8 ? 0.15 : 2 + 12 * (1 - st.density));
        // no building on a bank or a portal's approach: the ground rises there
        const banks = (o: number) => spanAt(s + o).structure === 'tunnel' || spanAt(s + o).structure === 'cutting';
        if (row >= st.rows || sp.structure !== 'ground' || banks(-60) || banks(60) || st.density <= 0 || (st.density < 0.1 && h3 > st.density * 3)) {
          s += Math.max(step, 8);
          continue;
        }
        // cross streets: a gap at regular blocks, so a downtown is not one long wall
        const block = st.plot[0] * 6;
        if (mod(s + seed, block) < 11 && st.density > 0.5) {
          s += 11;
          continue;
        }
        if (h3 > 0.5 + st.density * 0.6) {
          s += step;
          continue;
        }
        offBase = ROAD_HALF + st.setback + d / 2 + row * (d * 1.22 + 7);
        const p = trackAt(tr, s + w / 2),
          x = p.x - p.tz * side * offBase,
          z = p.z + p.tx * side * offBase,
          yaw = Math.atan2(p.tx, p.tz);
        const b = { x, z, yaw, w, d };
        const clear = corners(b).every(c => road(c[0], c[1]).d > ROAD_HALF + st.setback * 0.72) && road(x, z).d > ROAD_HALF + st.setback;
        if (clear && !prints.hits(b) && !wet(x, z, Math.hypot(w, d) / 2 + 4)) {
          const [f0, f1] = st.floors,
            floors = Math.round(f0 + (f1 - f0) * (h2 * 0.65 + (0.35 * row) / Math.max(1, st.rows - 1)));
          buildings.push({
            ...b,
            h: floors * 3.15 + h1 * 0.5,
            floors,
            facade: pick(st.facades, h1),
            roofColor: pick(st.roofs, h3),
            windows: st.windows,
            roof: st.roof,
            shops: st.shops && row === 0,
            seed: h2,
          });
          prints.add(b);
        }
        s += step;
      }
    }

  // ---- street furniture and street trees ----
  const furniture: PlannedFurniture[] = [];
  for (const side of [-1, 1])
    for (let s = side * 7 + 15; s < tr.L && furniture.length < budget.furniture; s += 14) {
      const sp = spanAt(s),
        st = styles[sp.district];
      if (sp.structure === 'tunnel') continue;
      const p = trackAt(tr, s),
        yaw = Math.atan2(p.tx, p.tz),
        h = hash(s, side + seed),
        at = (off: number) => [p.x - p.tz * side * off, p.z + p.tx * side * off] as const;
      const free = (x: number, z: number) => !prints.at(x, z, 0.6) && !wet(x, z, 1) && road(x, z).d > ROAD_HALF + 0.6;
      if (st.lamps && Math.round(s / 14) % 3 === 0) {
        const [x, z] = at(ROAD_HALF + 1.15);
        if (free(x, z)) furniture.push({ kind: 'lamp', x, z, yaw: yaw + (side > 0 ? Math.PI : 0), seed: h });
        continue;
      }
      if (sp.structure === 'bridge') continue;
      if (st.streetTrees && Math.round(s / 14) % 3 === 1 && h < 0.75) {
        const [x, z] = at(ROAD_HALF + 2.3);
        if (free(x, z)) furniture.push({ kind: 'tree', x, z, yaw: 0, seed: h });
        continue;
      }
      if (st.cars && h < 0.28) {
        const [x, z] = at(ROAD_HALF + 1.05);
        if (free(x, z)) furniture.push({ kind: 'car', x, z, yaw, seed: hash(h, s) });
        continue;
      }
      if (st.lamps && h > 0.93) {
        const [x, z] = at(ROAD_HALF + 2.5);
        if (free(x, z)) furniture.push({ kind: 'bench', x, z, yaw: yaw + (side > 0 ? -Math.PI / 2 : Math.PI / 2), seed: h });
      }
    }

  // ---- skyline: far silhouettes, never approached ----
  const horizon = world.horizon ?? 'mountains',
    skyline: WorldPlan['skyline'] = [];
  if (horizon === 'skyline')
    for (let i = 0; i < budget.skyline; i++) {
      const a = hash(seed, i) * Math.PI * 2,
        r = extent + 200 + hash(i, seed) ** 1.5 * 1500,
        x = cx + Math.cos(a) * r,
        z = cz + Math.sin(a) * r;
      if (wet(x, z, 30)) continue;
      skyline.push({
        x,
        z,
        w: 26 + hash(i, 2) * 40,
        d: 26 + hash(i, 3) * 40,
        h: 40 + hash(i, 4) ** 2 * 170,
        facade: pick(['#7f8ea0', '#8d97a3', '#a7a9ad', '#5f6b7a'], hash(i, 5)),
      });
    }

  return {
    world,
    horizon,
    styles,
    spans,
    water,
    buildings,
    furniture,
    landmarks,
    skyline,
    spanAt,
    road,
    waterAt,
    // the terrain asks at every vertex, right after asking the road: the query it already has spares a second lookup
    styleAt: (x, z, near = road(x, z)) => styles[near.s >= 0 ? spanAt(near.s).district : 0],
    occupied: (x, z, m = 1) => prints.at(x, z, m),
  };
}
